import {
  IonButton,
  IonContent,
  IonIcon,
  IonLoading,
  IonPage,
  IonRouterLink,
  IonText,
  NavContext,
  useIonActionSheet
} from '@ionic/react';
import React, { useContext, useMemo } from 'react';

import {
  ButtonContainer,
  Invalid,
  Loading,
  Logo,
  Maintenance,
  Padding,
  PageMeta,
  Spacer
} from '@/components/common';
import { RequestSummary } from '@/components/summary';
import { Clinic, getClinic } from '@/data/Clinics';
import { Dentist, getDentist } from '@/data/Dentists';
import { Request, getRequest, getRequestByCode } from '@/data/Requests';
import { useMaintenanceMode } from '@/hooks';
import { REQUEST_STATUS_OPTIONS } from '@/utils/constants';
import { formatDentistName } from '@/utils/helpers/formatDentistName';
import { formatMobileNumber } from '@/utils/helpers/formatMobileNumber';
import { isClinicName } from '@/utils/helpers/isClinicName';
import { getScheduleRoute } from '@/utils/routes';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { calendarOutline } from 'ionicons/icons';
import { useParams } from 'react-router-dom';

import './Appointment.css';

interface RouteParams {
  id: string;
}

const Appointment: React.FC = () => {
  const { id: requestId } = useParams<RouteParams>();
  const [present] = useIonActionSheet();
  const { navigate } = useContext(NavContext);
  const isMaintenanceMode = useMaintenanceMode();

  const {
    data: request,
    isFetched: loadedRequest
  }: UseQueryResult<Request, Error> = useQuery({
    queryKey: ['request', requestId],
    queryFn: async () => {
      const requestData =
        (await getRequestByCode(requestId)) || (await getRequest(requestId));
      if (!requestData) throw new Error('Request not found');
      return requestData;
    }
  });

  const {
    data: dentist,
    isFetched: loadedDentist
  }: UseQueryResult<Dentist, Error> = useQuery({
    queryKey: ['dentist', request?.dentist_id],
    queryFn: async () => {
      if (!request?.dentist_id) throw new Error('Dentist ID not found');
      const dentistData = await getDentist(request.dentist_id);
      if (!dentistData) throw new Error('Dentist not found');
      return dentistData;
    },
    enabled: !!request?.dentist_id
  });

  const {
    data: clinic,
    isFetched: loadedClinic
  }: UseQueryResult<Clinic, Error> = useQuery({
    queryKey: ['clinic', dentist?.clinic_id],
    queryFn: async () => {
      if (!dentist?.clinic_id) throw new Error('Clinic ID not found');
      const clinicData = await getClinic(dentist.clinic_id);
      if (!clinicData) throw new Error('Clinic not found');
      return clinicData;
    },
    enabled: !!dentist?.clinic_id
  });

  const loading = !loadedRequest || !loadedDentist || !loadedClinic;

  const isUsingClinicName = useMemo(() => {
    return isClinicName(`${dentist?.first_name} ${dentist?.last_name}`);
  }, [dentist?.first_name, dentist?.last_name]);

  const copy = useMemo(() => {
    const getMessage = () => {
      switch (request?.status) {
        case REQUEST_STATUS_OPTIONS.PENDING:
          return "Your appointment request is awaiting approval. We'll send you a message once it's confirmed.";
        case REQUEST_STATUS_OPTIONS.APPROVED:
          return 'Your appointment has been confirmed. Add it to your calendar now.';
        case REQUEST_STATUS_OPTIONS.RESCHEDULED:
          return `${formatDentistName({
            dentist,
            returnDentistLastName: true
          })} requested a reschedule. Please request a new appointment.`;
        case REQUEST_STATUS_OPTIONS.DECLINED:
          return 'Your appointment request has been declined. Please request a new appointment.';
        case REQUEST_STATUS_OPTIONS.CANCELLED:
          return 'Your appointment has been cancelled. Please request a new appointment.';
        default:
          return '';
      }
    };

    return {
      loadingMessage: 'Loading...',
      subHeader: 'YOUR APPOINTMENT DETAILS',
      header: (
        <>
          Appointment {isUsingClinicName ? 'at ' : 'with '}
          <IonText color="primary">
            {formatDentistName({
              dentist,
              returnDentistLastName: true
            })}
          </IonText>
        </>
      ),
      message: getMessage(),
      addToCalendarHeader: 'Add to Calendar',
      googleCalendar: 'Google Calendar',
      appleCalendar: 'Apple Calendar',
      cancel: 'Cancel',
      addToCalendarButton: 'Add to Calendar'
    };
  }, [request?.status, dentist]);

  const showButton = useMemo(() => {
    return request?.status === REQUEST_STATUS_OPTIONS.APPROVED;
  }, [request?.status]);

  const generateCalendarLink = (service: string) => {
    if (!request || !dentist) return '#';
    const { purpose, start_time, end_time, date } = request;
    const timezone = 'Asia/Manila';
    const start = `${date}T${start_time}`;
    const end = `${date}T${end_time}`;
    const title = `Appointment ${isUsingClinicName ? 'at' : 'with'} ${formatDentistName(
      {
        dentist,
        returnDentistLastName: true
      }
    )}`;
    return `https://calndr.link/d/event/?service=${service}&start=${start}&end=${end}&title=${title}&timezone=${timezone}&description=Purpose%3A%20${purpose}`;
  };

  const handleButtonClick = (service: string) => {
    const link = generateCalendarLink(service);
    window.open(link, '_blank');
  };

  const handleAddToCalendarClick = () => {
    present({
      header: copy.addToCalendarHeader,
      buttons: [
        {
          text: copy.googleCalendar,
          handler: () => handleButtonClick('google')
        },
        {
          text: copy.appleCalendar,
          handler: () => handleButtonClick('apple')
        },
        {
          text: copy.cancel,
          role: 'cancel'
        }
      ]
    });
  };

  const onRequest = () => {
    const scheduleRoute = getScheduleRoute(clinic?.id);
    navigate(scheduleRoute);
  };

  return (
    <IonPage id="appointment">
      {clinic && (
        <PageMeta
          title="Appointment at {clinic} - Odonto"
          description="View your appointment details at {clinic} with Odonto."
          clinic={clinic}
        />
      )}
      <IonContent>
        {loading ? (
          <Padding>
            <Logo />
            <Loading />
          </Padding>
        ) : (
          <Padding>
            <Logo />
            {isMaintenanceMode ? (
              <Maintenance />
            ) : request && dentist && clinic ? (
              <>
                <Spacer top={16} bottom={24}>
                  <h4 className="appointment__sub-header">{copy.subHeader}</h4>
                  <h1 className="appointment__header">{copy.header}</h1>
                  <p className="appointment__message">{copy.message}</p>
                </Spacer>
                <RequestSummary
                  clinic={clinic}
                  dentist={dentist}
                  request={request}
                  showPurpose
                  showStatus
                />
                <Spacer top={16} bottom={showButton ? 102 : 24}>
                  {showButton ? (
                    <div className="appointment__note">
                      <p>
                        To cancel or reschedule your appointment, please contact
                        your dentist at:{' '}
                        <IonText color="primary">
                          {formatMobileNumber(clinic?.mobile_number)}
                        </IonText>
                      </p>
                    </div>
                  ) : (
                    <div className="appointment__note">
                      <p>Request a new appointment?</p>
                      <IonRouterLink onClick={onRequest}>
                        Click here
                      </IonRouterLink>
                    </div>
                  )}
                </Spacer>
                {showButton && (
                  <ButtonContainer>
                    <IonButton
                      className={`appointment__add-to-calendar-button ${
                        request?.status === REQUEST_STATUS_OPTIONS.PENDING
                          ? 'disabled'
                          : ''
                      }`}
                      onClick={handleAddToCalendarClick}
                      expand="block"
                      fill="solid"
                      size="default"
                      strong
                    >
                      <IonIcon icon={calendarOutline} slot="start" />
                      {copy.addToCalendarButton}
                    </IonButton>
                  </ButtonContainer>
                )}
              </>
            ) : (
              <Invalid />
            )}
          </Padding>
        )}
      </IonContent>
    </IonPage>
  );
};

export default Appointment;
